import { ReactNode, useState, useEffect, useRef } from 'react';
import { Home, MessageSquare, BarChart2, Settings, PanelLeft, PanelRight, BookOpen, Megaphone, Archive, Lightbulb, Menu, X, ChevronDown, ChevronUp, CreditCard, LogOut, Gift, Share2, ArrowUpRight, HelpCircle } from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import { Link } from 'wouter';
import { useCoachingSessionStore } from '../lib/coachingSessionStore';
import MobileDrawer from './MobileDrawer';
import FloatingFeedbackButton from './FloatingFeedbackButton';
import WelcomeTour from './WelcomeTour';

interface AppLayoutProps {
  children: ReactNode;
  currentPage?: 'home' | 'coaching' | 'insights-hub' | 'settings' | 'feedback' | 'wishlist';
}

const AppLayout = ({ children, currentPage }: AppLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [isTourOpen, setIsTourOpen] = useState(false);
  const sessionId = useCoachingSessionStore((state) => state.sessionId);

  // Beta notification state
  const [showBetaNotification, setShowBetaNotification] = useState(false);
  const betaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Beta notification logic
  useEffect(() => {
    const dismissed = localStorage.getItem('hipoAiCoach_betaNotificationDismissed');
    if (!dismissed) {
      const timer = setTimeout(() => {
        setShowBetaNotification(true);
        // Auto-hide after 8 seconds
        betaTimerRef.current = setTimeout(() => {
          handleDismissBetaNotification();
        }, 8000);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cleanup timer on unmounts
  useEffect(() => {
    return () => {
      if (betaTimerRef.current) {
        clearTimeout(betaTimerRef.current);
      }
    };
  }, []);

  const handleDismissBetaNotification = () => {
    localStorage.setItem('hipoAiCoach_betaNotificationDismissed', 'true');
    setShowBetaNotification(false);
    if (betaTimerRef.current) {
      clearTimeout(betaTimerRef.current);
      betaTimerRef.current = null;
    }
  };

  // Toggle submenu expansion
  const toggleSubmenu = (menuId: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  // Handle recommend share action
  const handleRecommendShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'HiPoAiCoach - AI-Powered Personal Growth',
        text: 'I\'ve been using HiPoAiCoach for my professional development. Check out these AI coaches that help unlock effortless high performance!',
        url: window.location.origin
      });
    } else {
      navigator.clipboard.writeText(`Check out HiPoAiCoach - AI-Powered Personal Growth! ${window.location.origin}`);
      // Could add a toast notification here if needed
    }
  };

  // Handle quick tour action
  const handleQuickTour = () => {
    setIsTourOpen(true);
  };

  // Auto-popup tour for first-time users (only on home page)
  useEffect(() => {
    const checkFirstTimeUser = () => {
      try {
        const tourCompleted = localStorage.getItem('hipoTourCompleted');
        
        // If tour has never been completed and we're on home page, auto-popup after 500ms delay
        if (!tourCompleted && currentPage === 'home') {
          const timer = setTimeout(() => {
            setIsTourOpen(true);
          }, 500);

          return () => clearTimeout(timer);
        }
      } catch (error) {
        // Handle localStorage unavailability gracefully
        console.log('localStorage unavailable, skipping auto-popup tour');
      }
    };

    // Only run on component mount
    checkFirstTimeUser();
  }, [currentPage]);

  const navItems = [
    // PRIMARY NAVIGATION
    { id: 'home', icon: Home, label: 'Home', path: '/dashboard' },
    { id: 'coaching', icon: MessageSquare, label: 'Coaching', path: sessionId ? `/coaching/session?session=${sessionId}` : '/coaching' },
    
    // INSIGHTS HUB WITH SUBMENU
    { 
      id: 'insights-hub', 
      icon: Lightbulb, 
      label: 'Insights Hub', 
      path: '/insights-hub',
      submenu: [
        { id: 'insights', icon: BarChart2, label: 'Insights', path: '/insights' },
        { id: 'archive', icon: Archive, label: 'MoM Archive', path: '/archive' },
        { id: 'wishlist', icon: BookOpen, label: 'Action Ledger', path: '/wishlist' },
      ]
    },
    
    // MY ACCOUNT WITH SUBMENU
    { 
      id: 'settings', 
      icon: Settings, 
      label: 'My Account', 
      path: '/settings',
      submenu: [
        { id: 'settings-main', icon: Settings, label: 'Settings', path: '/settings' },
        { id: 'billing', icon: CreditCard, label: 'Subscription & Billing', path: '/payments#subscribe' },
        { id: 'logout', icon: LogOut, label: 'Logout', path: '/settings#signout' },
      ]
    },
    
    // SHARE HIPO WITH SUBMENU  
    { 
      id: 'share', 
      icon: Share2, 
      label: 'Share HiPo', 
      submenu: [
        { id: 'gift', icon: Gift, label: 'Gifting Module', path: '/payments' },
        { id: 'recommend', icon: Megaphone, label: 'Recommend', action: 'share' },
      ]
    },
    
    // QUICK TOUR SECTION
    { 
      id: 'quick-tour', 
      icon: HelpCircle, 
      label: 'Quick Tour', 
      action: 'tour'
    },
    
    // FEEDBACK SECTION
    { 
      id: 'feedback', 
      icon: Megaphone, 
      label: 'Feedback', 
      path: '/feedback'
    },
  ];

  return (
    <div className="flex min-h-screen bg-white font-body antialiased">
      {/* Beta Notification Toast */}
      {showBetaNotification && (
        <div className={`fixed top-0 left-0 right-0 z-[60] transform transition-transform duration-500 ${
          showBetaNotification ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="bg-royal-blue text-white px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-3">
                <span className="text-lg">🚀</span>
                <p className="text-sm font-medium !text-white">
                  Beta Release: Thanks for joining us early! Features will evolve as we perfect your coaching experience.
                </p>
              </div>
              <button
                onClick={handleDismissBetaNotification}
                className="ml-4 rounded-lg p-1 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Dismiss notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Desktop Sidebar */}
      <aside
        className={`relative z-30 hidden flex-col bg-white border-r border-alto-grey shadow-sm transition-all duration-300 lg:flex ${isSidebarOpen ? 'lg:w-72' : 'lg:w-20'
          }`}
      >
        {/* Sidebar Header */}
        <div className={`flex items-center px-6 py-8 ${isSidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {isSidebarOpen && (
            <Link href="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/hipo-logo.png"
                alt="HiPo AI Coach"
                className="h-10 w-10 object-contain"
              />
              <span className="ml-3 font-display text-xl font-bold text-royal-blue tracking-tight">
                HiPo
              </span>
            </Link>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-3 rounded-lg hover:bg-gray-50 text-gray-600 transition-all duration-200"
          >
            {isSidebarOpen ? <PanelLeft className="h-6 w-6" /> : <PanelRight className="h-6 w-6" />}
          </button>
        </div>

        {/* Enhanced Navigation with Submenus */}
        <nav className="flex-1 px-4 py-6 space-y-3">
          {navItems.map((item) => {
            const isActive = currentPage === item.id;
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus.has(item.id);
            
            return (
              <div key={item.id}>
                {/* Main Navigation Item */}
                <div className="flex items-center">
                  {item.action === 'tour' ? (
                    <button
                      onClick={handleQuickTour}
                      className={`flex items-center flex-1 px-4 py-3.5 rounded-lg text-sm font-display font-medium transition-all duration-200
                        ${isSidebarOpen ? 'justify-start' : 'justify-center'}
                        ${isActive
                          ? 'bg-royal-blue text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-royal-blue'}`}
                    >
                      <item.icon className={`h-5 w-5 ${isActive ? '!text-white' : ''}`} strokeWidth={1.5} />
                      {isSidebarOpen && (
                        <span className={`ml-4 font-display font-medium ${isActive ? '!text-white' : ''}`}>{item.label}</span>
                      )}
                    </button>
                  ) : item.path ? (
                    <Link
                      href={item.path}
                      className={`flex items-center flex-1 px-4 py-3.5 rounded-lg text-sm font-display font-medium transition-all duration-200
                        ${isSidebarOpen ? 'justify-start' : 'justify-center'}
                        ${isActive
                          ? 'bg-royal-blue text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-royal-blue'}`}
                    >
                      <item.icon className={`h-5 w-5 ${isActive ? '!text-white' : ''}`} strokeWidth={1.5} />
                      {isSidebarOpen && (
                        <span className={`ml-4 font-display font-medium ${isActive ? '!text-white' : ''}`}>{item.label}</span>
                      )}
                    </Link>
                  ) : (
                    <div
                      className={`flex items-center flex-1 px-4 py-3.5 rounded-lg text-sm font-display font-medium
                        ${isSidebarOpen ? 'justify-start' : 'justify-center'}
                        text-gray-700`}
                    >
                      <item.icon className="h-5 w-5" strokeWidth={1.5} />
                      {isSidebarOpen && (
                        <span className="ml-4 font-display font-medium">{item.label}</span>
                      )}
                    </div>
                  )}
                  {/* Submenu Toggle Button */}
                  {isSidebarOpen && hasSubmenu && (
                    <button
                      onClick={() => toggleSubmenu(item.id)}
                      className="p-2 rounded-lg hover:bg-gray-50 hover:shadow-sm transition-all duration-200 ml-2"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-royal-blue" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-700 hover:text-royal-blue transition-colors" />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Submenu Items */}
                {isSidebarOpen && hasSubmenu && isExpanded && (
                  <div className="ml-6 mt-2 space-y-1 border-l border-gray-200 pl-4">
                    {item.submenu.map((subItem) => (
                      <div key={subItem.id}>
                        {'action' in subItem && subItem.action === 'share' ? (
                          <button
                            onClick={handleRecommendShare}
                            className="flex items-center w-full px-3 py-2 text-sm text-gray-600 hover:text-royal-blue hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <subItem.icon className="h-4 w-4 mr-3" />
                            {subItem.label}
                          </button>
                        ) : (
                          <Link
                            href={subItem.path || '#'}
                            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-royal-blue hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <subItem.icon className="h-4 w-4 mr-3" />
                            {subItem.label}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Profile */}
        {isSidebarOpen && (
          <div className="px-6 py-6 border-t border-alto-grey hidden">
            <div className="flex items-center gap-3">
              <UserButton afterSignOutUrl="/" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-display font-medium text-gray-900 truncate">Your Account</p>
                <p className="text-xs font-body text-gray-600">Manage settings</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col">
        {/* Mobile Header */}
        <header className="flex items-center justify-between bg-white border-b border-alto-grey px-4 py-4 lg:hidden">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              className="rounded-lg p-2 hover:bg-gray-50 text-gray-700 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link href="/dashboard" className="flex items-center">
              <img
                src="/hipo-logo.png"
                alt="HiPo AI Coach"
                className="h-8 w-8 object-contain"
              />
              <span className="ml-2 font-display text-xl font-bold text-royal-blue tracking-tight">
                HiPo
              </span>
            </Link>
          </div>
          <UserButton afterSignOutUrl="/" />
        </header>

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto bg-white lg:pb-0 ${currentPage === 'coaching' ? 'p-0' : 'mobile-spacing-generous'}`}>
          {children}
        </main>

        {/* Mobile Drawer */}
        <MobileDrawer
          isOpen={isMobileDrawerOpen}
          onClose={() => setIsMobileDrawerOpen(false)}
          currentPage={currentPage}
        />

        {/* Floating Feedback Button - Mobiles Only */}
        <FloatingFeedbackButton />
      </div>

      {/* Welcome Tour - Available from any dashboard tab */}
      <WelcomeTour
        isOpen={isTourOpen}
        onClose={() => {
          setIsTourOpen(false);
          // Mark tour as completed when closed/dismissed - permanent flag
          try {
            localStorage.setItem('hipoTourCompleted', 'true');
          } catch (error) {
            console.log('localStorage unavailable, tour completion not tracked');
          }
        }}
        onComplete={() => {
          setIsTourOpen(false);
          // Mark tour as completed when finished - permanent flag
          try {
            localStorage.setItem('hipoTourCompleted', 'true');
          } catch (error) {
            console.log('localStorage unavailable, tour completion not tracked');
          }
        }}
      />
    </div>
  );
};

export default AppLayout;
